import axios from 'axios';

const CODE = 'selected_company_forbidden';

// Blob#text は jsdom（jest）に無いので FileReader で読む
function readBlobText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('Blob を読めませんでした'));
    reader.readAsText(blob);
  });
}

// instanceof ArrayBuffer は realm が違う（jest の jsdom など）と false になるので、型タグで見る
function isArrayBuffer(data: unknown): data is ArrayBuffer {
  return Object.prototype.toString.call(data) === '[object ArrayBuffer]';
}

async function readBody(data: unknown): Promise<unknown> {
  // responseType: 'blob'（稼働表出力など）・'arraybuffer'（委託契約書 PDF など）の呼び出しはエラー本文もその形で届く
  try {
    if (data instanceof Blob) return JSON.parse(await readBlobText(data)) as unknown;
    if (isArrayBuffer(data)) return JSON.parse(new TextDecoder().decode(data)) as unknown;
  } catch {
    return undefined;
  }
  return data;
}

// 切替先の会社への権限が外れたときの 403（API: SelectedCompanyReauthorization・log_web_issues#367）
export async function isSelectedCompanyForbidden(error: unknown): Promise<boolean> {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) return false;
  const body = await readBody(error.response.data);
  return typeof body === 'object' && body !== null && (body as { code?: unknown }).code === CODE;
}

export type SelectedCompanyForbiddenStore = {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => boolean;
  markForbidden: () => void;
};

export function createSelectedCompanyForbiddenStore(): SelectedCompanyForbiddenStore {
  let forbidden = false;
  const listeners = new Set<() => void>();
  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => forbidden,
    markForbidden: () => {
      if (forbidden) return;
      forbidden = true;
      listeners.forEach((listener) => listener());
    },
  };
}

// ダイアログのマウント前に検知しても失わないよう、検知状態は React の外に持つ
export const selectedCompanyForbiddenStore = createSelectedCompanyForbiddenStore();

// 画面ごとの onError / catch（約 340 箇所）を直さないため、該当エラーでは呼び出し元に返す Promise を settle させない。
// 画面側の通知・finally・react-query の retry は動かず、ダイアログから再読み込みして捨てる。
// React の描画前（src/index.tsx）に 1 回だけ登録する。描画後の useEffect では、先に始まった画面のリクエストを捕まえられない
// （axios はリクエスト開始時に interceptor の列を確定する）
export function installSelectedCompanyForbiddenInterceptor(): () => void {
  const id = axios.interceptors.response.use(undefined, async (error: unknown) => {
    if (await isSelectedCompanyForbidden(error)) {
      selectedCompanyForbiddenStore.markForbidden();
      return new Promise<never>(() => {});
    }
    throw error;
  });
  return () => axios.interceptors.response.eject(id);
}
